import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Alert, Snackbar,
  IconButton, Tooltip, Divider, FormControl, InputLabel, Select, MenuItem, InputAdornment
} from "@mui/material";
import { Add, Edit, Delete, Search, Refresh, Visibility } from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const emptyForm = {
  codigo: "", nombre: "", descripcion: "",
  precio_1: 0, precio_2: 0, precio_3: 0,
  iva: "15", tipo: "SERVICIO", activo: true,
};

export default function Servicios() {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [busqueda, setBusqueda] = useState("");
  const [formData, setFormData] = useState({ ...emptyForm });
  const [verServicio, setVerServicio] = useState(null);
  const [pagina, setPagina] = useState(0);
  const POR_PAGINA = 20;
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  useEffect(() => { cargar(); cargarCatalogo(); }, [busqueda]);

  const cargarCatalogo = async () => {
    try {
      const [catRes, marRes] = await Promise.all([
        axios.get(`${API_URL}/api/catalogo/categorias-servicios`, { headers: headers() }),
        axios.get(`${API_URL}/api/catalogo/marcas`, { headers: headers() })
      ]);
      setCategorias(Array.isArray(catRes.data) ? catRes.data : []);
      setMarcas(Array.isArray(marRes.data) ? marRes.data : []);
    } catch {}
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tipo: "SERVICIO" });
      if (busqueda) params.append("busqueda", busqueda);
      const res = await axios.get(`${API_URL}/api/productos?${params}`, { headers: headers() });
      setServicios(Array.isArray(res.data) ? res.data : []);
    } catch { setServicios([]); }
    setLoading(false);
  };

  const handleInput = (campo, valor) => {
    const upper = ["nombre", "descripcion"];
    const v = upper.includes(campo) && typeof valor === "string" ? valor.toUpperCase() : valor;
    setFormData(prev => ({ ...prev, [campo]: v }));
  };

  const abrirDialogo = (s = null) => {
    if (s) {
      setEditando(true); setSeleccionado(s);
      setFormData({ ...emptyForm, ...s, precio_1: s.precio_1 || s.precio_venta || 0 });
    } else { setEditando(false); setSeleccionado(null); setFormData({ ...emptyForm }); }
    setDialogOpen(true);
  };
  const cerrarDialogo = () => { setDialogOpen(false); setEditando(false); setSeleccionado(null); };

  const guardar = async () => {
    if (!formData.nombre) return showSnackbar("El nombre del servicio es requerido", "error");
    try {
      const url = editando ? `${API_URL}/api/productos/${seleccionado.id}` : `${API_URL}/api/productos`;
      const method = editando ? "put" : "post";
      const data = { 
        ...formData, 
        tipo: "SERVICIO",
        iva_porcentaje: (formData.iva === "0" ? "0%" : formData.iva === "15" ? "15%" : formData.iva + "%"),
      };
      await axios[method](url, data, { headers: headers() });
      showSnackbar(editando ? "Servicio actualizado" : "Servicio creado", "success");
      cerrarDialogo(); cargar();
    } catch (e) { showSnackbar(e.response?.data?.error || "Error al guardar", "error"); }
  };

  const eliminar = async (s) => {
    if (!window.confirm(`¿Eliminar servicio "${s.nombre}"?`)) return;
    try { await axios.delete(`${API_URL}/api/productos/${s.id}`, { headers: headers() }); showSnackbar("Eliminado", "success"); cargar(); }
    catch (e) { showSnackbar(e.response?.data?.error || "Error", "error"); }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>🔧 Servicios</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => abrirDialogo()}>Nuevo Servicio</Button>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField fullWidth size="small" placeholder="Buscar por código o nombre..."
            value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPagina(0); }}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }} />
        </Box>

        {loading ? <Typography>Cargando...</Typography> : servicios.length === 0 ? (
          <Alert severity="info">No hay servicios registrados.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Código</strong></TableCell>
                    <TableCell><strong>SKU</strong></TableCell>
                    <TableCell align="center"><strong>Img</strong></TableCell>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell><strong>Categoría</strong></TableCell>
                    <TableCell align="right"><strong>PVP</strong></TableCell>
                    <TableCell align="right"><strong>P1</strong></TableCell>
                    <TableCell align="right"><strong>P2</strong></TableCell>
                    <TableCell align="right"><strong>P3</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {servicios.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA).map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{s.codigo || "—"}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{s.codigo_auxiliar_sku || s.codigo_auxiliar || "—"}</Typography></TableCell>
                      <TableCell align="center">
                        {s.imagen_url ? (
                          <img src={s.imagen_url.startsWith("http") ? s.imagen_url : `${API_URL}/${s.imagen_url}`}
                            alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }} />
                        ) : (
                          <Box sx={{ width: 36, height: 36, bgcolor: "#f5f5f5", borderRadius: 1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{s.nombre}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{categorias.find(c => c.id === s.categoria_id)?.nombre || "—"}</Typography></TableCell>
                      <TableCell align="right"><strong>${parseFloat(s.pvp || s.precio_venta || s.precio_1 || 0).toFixed(2)}</strong></TableCell>
                      <TableCell align="right">${parseFloat(s.precio_1 || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${parseFloat(s.precio_2 || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${parseFloat(s.precio_3 || 0).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver"><IconButton size="small" onClick={() => setVerServicio(s)}><Visibility /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirDialogo(s)}><Edit /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminar(s)}><Delete /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.min(pagina * POR_PAGINA + 1, servicios.length)}–{Math.min((pagina + 1) * POR_PAGINA, servicios.length)} de {servicios.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" size="small" disabled={pagina === 0} onClick={() => setPagina(pagina - 1)}>← Anterior</Button>
                <Button variant="outlined" size="small" disabled={(pagina + 1) * POR_PAGINA >= servicios.length} onClick={() => setPagina(pagina + 1)}>Siguiente →</Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={cerrarDialogo} fullWidth PaperProps={{ sx: { borderRadius: 3, maxWidth: 900, width: "95vw" } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: 20 }}>{editando ? "✏️ Editar Servicio" : "➕ Nuevo Servicio"}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>

          {/* 1. Identificación */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Identificación y Códigos</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Código (Principal)" value={formData.codigo}
                onChange={(e) => handleInput("codigo", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} helperText="codigoPrincipal SRI" />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Código Auxiliar / SKU" value={formData.codigo_auxiliar || ""}
                onChange={(e) => handleInput("codigo_auxiliar", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} helperText="Código de referencia" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth required label="Nombre del Servicio" value={formData.nombre}
                onChange={(e) => handleInput("nombre", e.target.value)}
                inputProps={{ style: { textTransform: "uppercase", fontSize: 16 }, maxLength: 300 }}
                helperText="Máx. 300 caracteres" />
            </Grid>
          </Grid>

          {/* 2. Clasificación */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Clasificación</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select value={formData.categoria_id || ""} label="Categoría" onChange={(e) => handleInput("categoria_id", e.target.value)}>
                  <MenuItem value="">— Sin categoría —</MenuItem>
                  {categorias.map(c => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 3. Impuestos */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Impuestos</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>IVA</InputLabel>
                <Select value={formData.iva || "15"} label="IVA" onChange={(e) => handleInput("iva", e.target.value)}>
                  <MenuItem value="15">15%</MenuItem>
                  <MenuItem value="0">0%</MenuItem>
                  <MenuItem value="noobj">No Objeto de IVA</MenuItem>
                  <MenuItem value="exento">Exento de IVA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 4. Precios */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Precios (IVA incluido)</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="PVP" type="number" value={formData.pvp || 0}
                onChange={(e) => handleInput("pvp", parseFloat(e.target.value) || 0)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01, style: { fontSize: 16 } }}
                helperText="Precio de venta al público" />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Precio 1" type="number" value={formData.precio_1 || 0}
                onChange={(e) => handleInput("precio_1", parseFloat(e.target.value) || 0)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01, style: { fontSize: 16 } }} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Precio 2" type="number" value={formData.precio_2 || 0}
                onChange={(e) => handleInput("precio_2", parseFloat(e.target.value) || 0)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01, style: { fontSize: 16 } }} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Precio 3" type="number" value={formData.precio_3 || 0}
                onChange={(e) => handleInput("precio_3", parseFloat(e.target.value) || 0)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01, style: { fontSize: 16 } }} />
            </Grid>
          </Grid>

          {/* 5. Descripción e Imagen */}
          <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>Descripción e Imagen</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Descripción" value={formData.descripcion || ""}
                onChange={(e) => handleInput("descripcion", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} multiline rows={2}
                placeholder="Descripción del servicio" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Detalles del Servicio" value={formData.ficha_tecnica || ""}
                onChange={(e) => handleInput("ficha_tecnica", e.target.value)}
                inputProps={{ style: { fontSize: 16 } }} multiline rows={2}
                placeholder="Qué incluye, condiciones, tiempos..." />
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button variant="outlined" component="label" sx={{ height: 56 }}>
                  📷 Subir Imagen
                  <input type="file" hidden accept="image/*" onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("imagen", file);
                    try {
                      const res = await axios.post(`${API_URL}/api/productos/upload-imagen`, fd, {
                        headers: { ...headers(), "Content-Type": "multipart/form-data" }
                      });
                      if (res.data.url) handleInput("imagen_url", res.data.url);
                    } catch { showSnackbar("Error al subir imagen", "error"); }
                  }} />
                </Button>
                {formData.imagen_url && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <img src={formData.imagen_url.startsWith("http") ? formData.imagen_url : `${API_URL}/${formData.imagen_url}`}
                      alt="servicio" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }} />
                    <Button size="small" color="error" onClick={() => handleInput("imagen_url", "")}>✕</Button>
                  </Box>
                )}
                {!formData.imagen_url && <Typography variant="caption" color="text.secondary">Sin imagen</Typography>}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={cerrarDialogo} color="inherit">Cancelar</Button>
          <Button onClick={guardar} variant="contained" size="large">{editando ? "Actualizar" : "Crear"} Servicio</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ver */}
      <Dialog open={!!verServicio} onClose={() => setVerServicio(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {verServicio && (<>
          <DialogTitle sx={{ fontWeight: 700 }}>👁️ {verServicio.nombre}</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Identificación</Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="body2"><strong>Código:</strong> {verServicio.codigo || "—"}</Typography>
                  <Typography variant="body2"><strong>SKU / Auxiliar:</strong> {verServicio.codigo_auxiliar_sku || verServicio.codigo_auxiliar || "—"}</Typography>
                  <Typography variant="body2"><strong>Nombre:</strong> {verServicio.nombre}</Typography>
                  <Typography variant="body2"><strong>Categoría:</strong> {categorias.find(c => c.id === verServicio.categoria_id)?.nombre || "—"}</Typography>
                </Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Precios</Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="body2"><strong>PVP:</strong> ${parseFloat(verServicio.pvp || verServicio.precio_venta || verServicio.precio_1 || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>Precio 1:</strong> ${parseFloat(verServicio.precio_1 || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>Precio 2:</strong> ${parseFloat(verServicio.precio_2 || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>Precio 3:</strong> ${parseFloat(verServicio.precio_3 || 0).toFixed(2)}</Typography>
                  <Typography variant="body2"><strong>IVA:</strong> {verServicio.iva || "15"}%</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {verServicio.imagen_url && (
                  <Box sx={{ mb: 2, textAlign: "center" }}>
                    <img src={verServicio.imagen_url.startsWith("http") ? verServicio.imagen_url : `${API_URL}/${verServicio.imagen_url}`}
                      alt={verServicio.nombre} style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 8, border: "1px solid #eee" }} />
                  </Box>
                )}
                <Typography variant="subtitle2" color="primary" gutterBottom>Detalles del Servicio</Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{verServicio.ficha_tecnica || "—"}</Typography>
                </Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>Descripción</Typography>
                <Box sx={{ pl: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{verServicio.descripcion || "—"}</Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setVerServicio(null); abrirDialogo(verServicio); }} startIcon={<Edit />}>Editar</Button>
            <Button onClick={() => setVerServicio(null)} variant="contained">Cerrar</Button>
          </DialogActions>
        </>)}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
